// packages/ui/src/Button.tsx

import React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from './Tooltip';

// --- 1. Define all style variants using CVA ---
// Base classes support both themes - neobrutalism is default, liquid-glass activates via body class
const buttonVariants = cva(
  [
    // Base structure (shared)
    'relative inline-flex items-center justify-center gap-2',
    'font-bold',
    'transition-all duration-200 transform',
    'disabled:opacity-60 disabled:cursor-not-allowed',
    'focus:outline-none',

    // Neobrutalism base (default)
    'border-2 border-black rounded-xl',
    'hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgb(17,24,39)]',
    'active:translate-x-0 active:translate-y-0 active:shadow-none',
    'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-500',
    'disabled:hover:shadow-none disabled:hover:translate-x-0 disabled:hover:translate-y-0',

    // Liquid glass overrides (when .theme-liquid-glass is on body)
    '[.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20 [.theme-liquid-glass_&]:rounded-2xl',
    '[.theme-liquid-glass_&]:hover:translate-x-0 [.theme-liquid-glass_&]:hover:-translate-y-0.5',
    '[.theme-liquid-glass_&]:hover:shadow-[0_8px_24px_rgba(139,92,246,0.25)]',
    '[.theme-liquid-glass_&]:active:translate-y-0 [.theme-liquid-glass_&]:active:shadow-[0_4px_16px_rgba(0,0,0,0.15)]',
    '[.theme-liquid-glass_&]:focus-visible:ring-violet-400 [.theme-liquid-glass_&]:focus-visible:ring-offset-slate-900',
  ],
  {
    variants: {
      variant: {
        primary: [
          // Neobrutalism
          'bg-emerald-500 text-white hover:bg-emerald-400 active:bg-emerald-600',
          // Liquid glass
          '[.theme-liquid-glass_&]:bg-violet-500/80 [.theme-liquid-glass_&]:hover:bg-violet-400/80 [.theme-liquid-glass_&]:active:bg-violet-600/80',
          '[.theme-liquid-glass_&]:backdrop-blur-sm',
        ],
        secondary: [
          // Neobrutalism
          'bg-gray-100 text-gray-900 hover:bg-gray-50 active:bg-gray-200',
          // Liquid glass
          '[.theme-liquid-glass_&]:bg-white/10 [.theme-liquid-glass_&]:text-white',
          '[.theme-liquid-glass_&]:hover:bg-white/20 [.theme-liquid-glass_&]:active:bg-white/5',
        ],
        outline: [
          // Neobrutalism
          'bg-white text-gray-900 hover:bg-gray-50 active:bg-gray-100',
          // Liquid glass
          '[.theme-liquid-glass_&]:bg-transparent [.theme-liquid-glass_&]:text-white',
          '[.theme-liquid-glass_&]:border-white/30',
          '[.theme-liquid-glass_&]:hover:bg-white/10 [.theme-liquid-glass_&]:active:bg-white/5',
        ],
        ghost: [
          // Neobrutalism
          'bg-transparent text-slate-700 border-transparent hover:border-black hover:bg-gray-50 active:bg-gray-100',
          // Liquid glass
          '[.theme-liquid-glass_&]:text-white/80 [.theme-liquid-glass_&]:border-transparent',
          '[.theme-liquid-glass_&]:hover:border-transparent [.theme-liquid-glass_&]:hover:bg-white/10',
          '[.theme-liquid-glass_&]:active:bg-white/5',
        ],
        danger: [
          // Neobrutalism
          'bg-red-600 text-white hover:bg-red-500 active:bg-red-700',
          // Liquid glass
          '[.theme-liquid-glass_&]:bg-rose-500/80',
          '[.theme-liquid-glass_&]:hover:bg-rose-400/80 [.theme-liquid-glass_&]:active:bg-rose-600/80',
        ],
      },
      size: {
        xs: 'px-3 py-1.5 text-xs',
        sm: 'px-4 py-2 text-sm',
        md: 'px-6 py-2.5 text-base',
        lg: 'px-8 py-3 text-lg',
        xl: 'px-10 py-4 text-xl',
        '2xl': 'px-12 py-5 text-2xl',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

// --- 2. Define Component Props ---
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  text?: string;
  textKey?: string;
  textOptions?: Record<string, unknown>;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  ariaLabel?: string;
  ariaLabelKey?: string;
  badge?: number;
  badgeColor?: 'red' | 'green' | 'blue' | 'yellow';
  tooltip?: string;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  asChild?: boolean;
  loading?: boolean;
}

// --- 3. Define the Badge Sub-component ---
const Badge = ({ count, color }: { count: number; color: NonNullable<ButtonProps['badgeColor']> }) => {
  const badgeColorClasses: Record<NonNullable<ButtonProps['badgeColor']>, string> = {
    red: 'bg-red-500',
    green: 'bg-emerald-500',
    blue: 'bg-blue-500',
    yellow: 'bg-amber-500',
  };

  return (
    <span
      className={`absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center text-xs font-bold text-white rounded-full border-2 border-black [.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/30 ${badgeColorClasses[color]}`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
};

// --- 4. Main Button Component ---
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      asChild = false,
      text,
      textKey,
      textOptions,
      icon,
      iconPosition = 'left',
      ariaLabel,
      ariaLabelKey,
      badge,
      badgeColor = 'red',
      tooltip,
      tooltipPosition = 'top',
      loading = false,
      children,
      disabled = false,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const Comp = asChild ? Slot : 'button';

    const buttonText = textKey ? t(textKey, textOptions) : text;
    const hasVisibleLabel = buttonText || children;

    let resolvedAriaLabel = ariaLabelKey ? t(ariaLabelKey) : ariaLabel;
    if (!resolvedAriaLabel && !asChild) {
      resolvedAriaLabel = buttonText ?? (typeof children === 'string' ? children : undefined);
    }

    const showBadge = badge !== undefined && badge > 0;

    const buttonContent = (
      <>
        {loading && <Loader2 className="h-5 w-5 animate-spin" />}
        {!loading && icon && iconPosition === 'left' && <div className="relative">{icon}{showBadge && <Badge count={badge} color={badgeColor} />}</div>}
        {!loading && hasVisibleLabel && (<span>{buttonText || children}</span>)}
        {!loading && icon && iconPosition === 'right' && <div className="relative">{icon}{showBadge && <Badge count={badge} color={badgeColor} />}</div>}
      </>
    );

    const buttonElement = (
      <Comp
        className={buttonVariants({ variant, size, fullWidth, className })}
        ref={ref}
        disabled={disabled || loading}
        aria-label={resolvedAriaLabel}
        type={type}
        {...props}
      >
        {asChild ? children : buttonContent}
      </Comp>
    );

    if (tooltip) {
      return (
        <Tooltip text={tooltip} position={tooltipPosition} disabled={disabled || loading}>
          {buttonElement}
        </Tooltip>
      );
    }

    return buttonElement;
  }
);

Button.displayName = 'Button';
